import { Post, Tournament } from '../models';
import { MatchRepository, ParticipantRepository, PostRepository, TournamentRepository } from '../repositories';
import { logger } from '@/src/core/utils';
import { ModerateContentUseCase } from './ModerateContentUseCase';

export class CreateVictoryPostUseCase {
  constructor(
    private postRepository: PostRepository,
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository,
    private moderateContent: ModerateContentUseCase,
  ) {}

  async execute(tournamentId: string, customMessage?: string): Promise<Post | null> {
    logger.debug('VictoryPost', 'Starting victory post creation', { tournamentId });

    // 1. Get the tournament
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      logger.debug('VictoryPost', 'Tournament not found');
      return null;
    }

    if (tournament.status !== 'completed') {
      logger.debug('VictoryPost', 'Tournament not completed');
      return null;
    }

    // 2. Get the champion
    const champion = await this.getChampion(tournament);
    if (!champion) {
      logger.debug('VictoryPost', 'No champion found');
      return null;
    }

    logger.debug('VictoryPost', 'Champion identified', { name: champion.name, userId: champion.userId });

    // 3. Only create post if champion is a registered user
    if (!champion.userId) {
      logger.debug('VictoryPost', 'Champion is not a registered user, skipping post creation');
      return null;
    }

    // 4. Create the victory post
    const content = customMessage || `🏆 I won the tournament "${tournament.name}"!`;

    // Moderate custom message content (skip auto-generated messages)
    if (customMessage) {
      await this.moderateContent.execute(customMessage, 'post_content', champion.userId);
    }

    const post = await this.postRepository.create(
      champion.userId,
      content,
      undefined, // no image
      tournamentId
    );

    logger.info('VictoryPost', 'Victory post created', { tournamentId, postId: post.id });
    return post;
  }

  private async getChampion(tournament: Tournament) {
    const matches = await this.matchRepository.getByTournamentId(tournament.id);

    // Double Elimination - get champion from grand final
    const grandFinals = matches
      .filter(m => m.bracketType === 'grand_final' && m.status === 'completed')
      .sort((a, b) => b.round - a.round);

    if (grandFinals.length > 0 && grandFinals[0].winnerId) {
      return this.participantRepository.getById(grandFinals[0].winnerId);
    }

    // Single Elimination - find the last completed winners bracket match (no next match)
    const winnersMatches = matches
      .filter(m => m.bracketType === 'winners' && m.status === 'completed')
      .sort((a, b) => b.round - a.round);

    const finalMatch = winnersMatches.find(m => !m.nextMatchId);
    if (finalMatch && finalMatch.winnerId) {
      return this.participantRepository.getById(finalMatch.winnerId);
    }

    // Round Robin - get the participant with most points
    if (tournament.tournamentType === 'round_robin' || tournament.tournamentType === 'swiss') {
      const participants = await this.participantRepository.getByTournamentId(tournament.id);

      // Calculate standings
      const standings = participants.map(participant => {
        const participantMatches = matches.filter(
          m => m.status === 'completed' &&
               (m.participant1Id === participant.id || m.participant2Id === participant.id)
        );

        let points = 0;
        let wins = 0;

        participantMatches.forEach(match => {
          if (match.winnerId === participant.id) {
            wins++;
            points += 3;
          } else if (match.scoreParticipant1 === match.scoreParticipant2) {
            points += 1; // Draw
          }
        });

        return { participant, points, wins };
      });

      standings.sort((a, b) => b.points - a.points || b.wins - a.wins);

      if (standings.length > 0 && standings[0].points > 0) {
        return standings[0].participant;
      }
    }

    return null;
  }
}
